
import React, { useState, useEffect } from 'react';
import { ReadingStats } from '../types';
import { MembershipTier } from '../types/membership';
import { FileText, Upload, FolderOpen, BookOpen } from 'lucide-react';
import { FileImport } from '../components/FileImport';
import { ContentLibrary } from '../components/ContentLibrary';
import StorySelectionModal from '../components/StorySelectionModal';
import { BookSelectionModal } from '../components/BookSelectionModal';
import { BookCard } from '../components/BookCard';
import { useNavigate } from 'react-router-dom';
import { CategoryItem, CategoryItemSectioned } from '../types/category';
import { ContentSection, ProcessedBookResult, ProcessedBookChapter } from '../types/content';
import { ChapterInfo, saveBook, getBook, StoredProcessedBook, deleteBook } from '../utils/indexedDB';
import { useMultiBookProgress } from '../hooks/useMultiBookProgress';
import { usePageMeta } from '../hooks/usePageMeta';

interface LibraryPageProps {
  stats: ReadingStats;
  currentWpm: number;
  initialContent: string;
  onStartReading: (
    content: string,
    sections?: ContentSection[],
    categoryId?: string,
    bookId?: string,
    bookTitle?: string,
    bookMetadata?: {
      totalWords?: number;
      author?: string;
      coverUrl?: string;
      chapters?: ChapterInfo[];
      sections?: ContentSection[];
      categoryId?: string;
    },
    initialWordIndex?: number,
    format?: string
  ) => void;
  onStartBookReading?: (content: string, title: string) => void;
  userTier?: MembershipTier;
}

type LibraryTab = 'import' | 'categories' | 'book-preview' | 'my-books';

const LibraryPage: React.FC<LibraryPageProps> = ({ stats, currentWpm, initialContent, onStartReading, onStartBookReading, userTier = 'guest' }) => {
  usePageMeta({
    title: 'Import Text to Read & EPUB Reader Online | Flash-Read',
    description: 'Import your own text files (.txt, .epub) for RSVP speed reading. Browse 500+ built-in stories across 25 genres. Your personal reading library with progress tracking.',
  });

  const [inputText, setInputText] = useState(initialContent);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<LibraryTab>('categories');
  const [importedBook, setImportedBook] = useState<ProcessedBookResult | null>(null);
  const navigate = useNavigate();

  // Multi-book progress hook for "My Books" tab
  const { allProgress, isLoading: progressLoading, refreshProgress, saveBookProgress } = useMultiBookProgress();

  // State for imported books full data (keyed by bookId)
  const [importedBooksMap, setImportedBooksMap] = useState<Record<string, StoredProcessedBook>>({});

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalItem, setModalItem] = useState<{
    title: string;
    content: string;
    sections?: ContentSection[];
    categoryId?: string;
    difficulty: string;
    wordCount: number;
    readingTimeMinutes: number;
    source?: string;
    spoilerFreeSummary?: string;
    bookId?: string; // NEW: book identifier for progress tracking
  } | null>(null);

  // Load full imported books data when progress loads
  useEffect(() => {
    const loadImportedBooks = async () => {
      const importedProgress = allProgress.filter(p => p.bookId.startsWith('imported-'));
      if (importedProgress.length === 0) return;

      const books: Record<string, StoredProcessedBook> = {};
      await Promise.all(
        importedProgress.map(async (p) => {
          try {
            const book = await getBook(p.bookId);
            if (book) {
              books[p.bookId] = book;
            }
          } catch (err) {
            console.error('Failed to load imported book:', p.bookId, err);
          }
        })
      );
      setImportedBooksMap(books);
    };

    loadImportedBooks();
  }, [allProgress]);

  // Book selection state
  const [selectedChapters, setSelectedChapters] = useState<ProcessedBookChapter[]>([]);
  const [isBookSelectionOpen, setIsBookSelectionOpen] = useState(false);

  const handleFileLoaded = async (book: ProcessedBookResult) => {
    // Generate a unique bookId for imported books
    const importedBookId = `imported-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Enhance book with required fields for BookCard and storage
    const bookWithId: StoredProcessedBook = {
      ...book,
      bookId: importedBookId,
      id: importedBookId, // duplicate for BookCard compatibility
      totalChapters: book.chapters.length,
    };
    setImportedBook(bookWithId);

    // Save full book data to IndexedDB for offline access
    await saveBook(bookWithId);

    // Initialize reading progress for this book
    const firstChapter = book.chapters[0];
    try {
      await saveBookProgress(
        importedBookId,
        book.title,
        firstChapter.id,
        firstChapter.title,
        0, // start at beginning
        0, // no words read yet
        {
          totalChapters: book.chapters.length,
          totalWords: book.totalWords,
          author: book.author,
          chapters: book.chapters.map(ch => ({
            chapterId: ch.id,
            chapterTitle: ch.title,
            wordCount: ch.wordCount,
          })),
        }
      );

      // Add to local map so it appears immediately in My Books
      setImportedBooksMap(prev => ({
        ...prev,
        [importedBookId]: bookWithId,
      }));
    } catch (err) {
      console.error('Failed to initialize book progress:', err);
    }

    // If this is an EPUB with multiple chapters, show chapter selection
    if (book.source === 'epub' && book.chapters.length > 1) {
      setSelectedChapters(book.chapters);
      setIsBookSelectionOpen(true);
    } else {
      setActiveTab('book-preview');
    }
  };

  const handleStartReadingFromPreview = () => {
    if (!importedBook) return;

    // Convert ProcessedBookChapter[] to ContentSection[]
    const sections: ContentSection[] = importedBook.chapters.map((ch, i) => {
      const startWordIndex = importedBook.chapters.slice(0, i).reduce((sum, c) => sum + c.wordCount, 0);
      return {
        id: ch.id,
        title: ch.title,
        content: ch.content,
        wordCount: ch.wordCount,
        startWordIndex,
        endWordIndex: startWordIndex + ch.wordCount - 1,
      };
    });

    // Build book metadata
    const bookMetadata = {
      totalWords: importedBook.totalWords,
      author: importedBook.author,
      chapters: importedBook.chapters.map(ch => ({
        chapterId: ch.id,
        chapterTitle: ch.title,
        wordCount: ch.wordCount,
      })) as ChapterInfo[],
      // No coverUrl for now
    };

    onStartReading(
      importedBook.fullText,
      sections,
      undefined,
      importedBook.bookId, // generated UUID
      importedBook.title,
      bookMetadata,
      undefined, // initialWordIndex
      importedBook.source // format: from book source
    );
    setImportedBook(null);
    setActiveTab('categories');
  };

  const handleFileError = (errorMessage: string) => {
    setError(errorMessage);
  };

  // Handle book chapter selection
  const handleBookChapterSelect = (chapterId: string, content: string) => {
    // Convert selected chapter to ContentSection format
    const section: ContentSection = {
      id: chapterId,
      title: selectedChapters.find(c => c.id === chapterId)?.title || chapterId,
      content,
      wordCount: content.split(/\s+/).filter(w => w.length > 0).length,
      startWordIndex: 0,
      endWordIndex: content.split(/\s+/).filter(w => w.length > 0).length - 1,
    };

    // Build minimal metadata (we have only this chapter's info)
    const bookMetadata = {
      totalWords: importedBook?.totalWords,
      author: importedBook?.author,
      chapters: importedBook?.chapters.map(ch => ({
        chapterId: ch.id,
        chapterTitle: ch.title,
        wordCount: ch.wordCount,
      })) as ChapterInfo[],
    };

    // Start reading with the selected chapter, passing the imported book's identity
    onStartReading(
      content,
      [section],
      undefined,
      importedBook?.bookId, // Use the generated bookId
      importedBook?.title,
      bookMetadata,
      undefined, // initialWordIndex
      importedBook?.source // format
    );
    setIsBookSelectionOpen(false);
    setImportedBook(null);
    setActiveTab('categories');
  };

  const handleCloseBookSelection = () => {
    setIsBookSelectionOpen(false);
    setImportedBook(null);
    setActiveTab('categories');
  };

  // Handle selecting a category item to read
  const handleSelectCategoryItem = (item: CategoryItemSectioned) => {
    console.log('[LibraryPage] handleSelectCategoryItem called:', {
      title: item.title,
      hasSections: !!item.sections,
      sectionsCount: item.sections?.length,
      categoryId: item.categoryId,
      bookId: item.id // CategoryItem.id is the stable book identifier
    });
    
    setModalItem({
      title: item.title,
      content: item.content,
      sections: item.sections,
      categoryId: item.categoryId,
      difficulty: item.difficulty,
      wordCount: item.wordCount,
      readingTimeMinutes: item.readingTimeMinutes,
      source: item.source,
      spoilerFreeSummary: item.spoilerFreeSummary,
      bookId: item.id // Capture the stable book ID
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalItem(null);
  };

  const handleStartReadingFromModal = () => {
    console.log('[LibraryPage] handleStartReadingFromModal called');
    if (modalItem) {
      console.log('[LibraryPage] modalItem:', {
        title: modalItem.title,
        hasSections: !!modalItem.sections,
        sectionsCount: modalItem.sections?.length,
        categoryId: modalItem.categoryId,
        bookId: modalItem.bookId
      });
      // Build book metadata for progress tracking
      const bookMetadata = {
        totalWords: modalItem.wordCount,
        chapters: modalItem.sections?.map(s => ({
          chapterId: s.id,
          chapterTitle: s.title,
          wordCount: s.wordCount,
        })) as ChapterInfo[],
        categoryId: modalItem.categoryId,
        sections: modalItem.sections, // store section metadata for reload
      };
      onStartReading(
        modalItem.content,
        modalItem.sections,
        modalItem.categoryId,
        modalItem.bookId, // Pass bookId
        modalItem.title,  // Pass book title
        bookMetadata,     // Pass metadata
        undefined,       // initialWordIndex
        'txt'             // format: library stories are plain text
      );
      handleCloseModal();
    }
  };

  // Update tab handlers
  const handleTabChange = (tab: LibraryTab) => {
    setActiveTab(tab);
  };

  // Handle continue reading from "My Books" tab
  const handleContinueReading = async (bookId: string, chapterId: string, wordIndex: number) => {
    // Check if this is an imported book
    if (bookId.startsWith('imported-')) {
      const book = importedBooksMap[bookId];
      if (!book) {
        console.error('Imported book not found in map:', bookId);
        return;
      }
      // Convert chapters to ContentSection[] (same as in handleStartReadingFromPreview)
      const sections: ContentSection[] = book.chapters.map((ch, i) => {
        const startWordIndex = book.chapters.slice(0, i).reduce((sum, c) => sum + c.wordCount, 0);
        return {
          id: ch.id,
          title: ch.title,
          content: ch.content,
          wordCount: ch.wordCount,
          startWordIndex,
          endWordIndex: startWordIndex + ch.wordCount - 1,
        };
      });

      const bookMetadata = {
        totalWords: book.totalWords,
        author: book.author,
        chapters: book.chapters.map(ch => ({
          chapterId: ch.id,
          chapterTitle: ch.title,
          wordCount: ch.wordCount,
        })) as ChapterInfo[],
      };

      onStartReading(
        book.fullText,
        sections,
        undefined,
        book.bookId,
        book.title,
        bookMetadata,
        wordIndex,
        book.source // format
      );
    } else {
      // Library story: use stored sections (with contentPath) to lazy-load
      const progress = allProgress.find(p => p.bookId === bookId);
      if (!progress) return;

      // Use stored sections metadata; they have contentPath for lazy loading
      const sections = progress.sections || undefined;

      // Build minimal metadata
      const bookMetadata = {
        totalWords: progress.totalWords,
        author: progress.author || undefined,
        chapters: progress.chapters || undefined,
        sections: sections,
      };

      onStartReading(
        '', // content not needed when using sections
        sections,
        progress.categoryId,
        progress.bookId,
        progress.bookTitle,
        bookMetadata,
        wordIndex,
        'txt' // format: library stories are plain text files
      );
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300">


      {/* Book Selection Modal */}
      <BookSelectionModal
        isOpen={isBookSelectionOpen}
        onClose={handleCloseBookSelection}
        onStartReading={handleBookChapterSelect}
        title={importedBook?.title || 'Imported Book'}
        chapters={selectedChapters}
      />

      {/* Story Selection Modal */}
      <StorySelectionModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onStartReading={handleStartReadingFromModal}
        title={modalItem?.title || ''}
        content={modalItem?.content || ''}
        wordCount={modalItem?.wordCount || 0}
        readingTimeMinutes={modalItem?.readingTimeMinutes || 0}
        difficulty={modalItem?.difficulty || 'beginner'}
        source={modalItem?.source}
        spoilerFreeSummary={modalItem?.spoilerFreeSummary}
        userTier={userTier}
        bookId={modalItem?.bookId}
        bookTitle={modalItem?.title}
      />

      <div className="pt-28 pb-12 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Content Library</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2"><strong>Import text to read</strong> from your own files — our <strong>EPUB reader online</strong> and <strong>TXT file reader</strong> make it easy. Build your <strong>personal content reader</strong> library with <strong>custom reading material</strong> across 25+ genres, or upload your own documents for RSVP speed reading.</p>
        </div>
        
      </div>

      {/* Content Preparation Section */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Reading Material</h2>
        
        {/* Toggle Buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => handleTabChange('categories')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'categories'
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <FolderOpen className="h-4 w-4" />
            Categories
          </button>
           <button
             onClick={() => handleTabChange('import')}
             className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
               activeTab === 'import'
                 ? 'bg-blue-600 text-white'
                 : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
             }`}
           >
             <Upload className="h-4 w-4" />
             Import File
           </button>
           <button
             onClick={() => handleTabChange('my-books')}
             className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
               activeTab === 'my-books'
                 ? 'bg-blue-600 text-white'
                 : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
             }`}
           >
             <BookOpen className="h-4 w-4" />
             My Books
           </button>
           {importedBook && (
             <button
               onClick={() => handleTabChange('book-preview')}
               className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                 activeTab === 'book-preview'
                   ? 'bg-blue-600 text-white'
                   : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
               }`}
             >
               <FileText className="h-4 w-4" />
               Book Preview
             </button>
           )}
        </div>

        {/* Tab Content */}
        {activeTab === 'categories' && (
          /* Categories View */
          <ContentLibrary onSelectItem={handleSelectCategoryItem} />
        )}

        {activeTab === 'import' && (
          /* File Import View */
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Import from File</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              Upload .txt or .epub files. You can choose to read immediately or process for enhanced reading features.
            </p>
    <FileImport 
      onFileLoaded={handleFileLoaded} 
      onError={handleFileError}
      tier={userTier}
    />
            {error && <p className="text-xs text-red-600 mt-4">{error}</p>}
          </div>
        )}

        {activeTab === 'book-preview' && importedBook && (
          /* Book Preview */
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">{importedBook?.title}</h3>
              <span className="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded-full uppercase font-medium text-slate-600 dark:text-slate-300">
                {importedBook?.source}
              </span>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
              {importedBook?.chapters.length} {importedBook?.chapters.length === 1 ? 'chapter' : 'chapters'} · {importedBook?.totalWords.toLocaleString()} words
            </p>

            {importedBook?.warnings && (importedBook?.warnings?.length || 0) > 0 && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                {importedBook?.warnings?.map((w, i) => (
                  <p key={i} className="text-sm text-amber-700 dark:text-amber-400">{w}</p>
                ))}
              </div>
            )}

            <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
              {importedBook?.chapters.map(ch => (
                <div key={ch.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <span className="font-medium text-slate-900 dark:text-white text-sm">{ch.title}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">{ch.wordCount.toLocaleString()} words</span>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleStartReadingFromPreview}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Read from Start
              </button>
              <button
                onClick={() => {
                  setImportedBook(null);
                  setActiveTab('import');
                }}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg font-medium transition-colors"
              >
                Import Another
              </button>
            </div>
          </div>
        )}

        {activeTab === 'my-books' && (
          /* My Books Tab */
          <div className="space-y-4">
            {progressLoading ? (
              <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                Loading your library...
              </div>
            ) : allProgress.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
                <BookOpen className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                <p className="text-slate-600 dark:text-slate-400">Your library is empty.</p>
                <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">Start reading to see your books here.</p>
              </div>
            ) : (
              allProgress.map((progress) => {
                const isImported = progress.bookId.startsWith('imported-');
                const book = isImported ? importedBooksMap[progress.bookId] : null;
                
                // Guard: if imported book is still loading, show placeholder
                if (isImported && !book) {
                  return (
                    <div key={progress.bookId} className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                      <div className="animate-pulse space-y-3">
                        <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-3/4"></div>
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div>
                        <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mt-4"></div>
                      </div>
                    </div>
                  );
                }

                const libraryBookLike = isImported ? null : {
                  id: progress.bookId,
                  title: progress.bookTitle,
                  author: progress.author || undefined,
                  totalWords: progress.totalWords || 0,
                  totalChapters: progress.chapters?.length || 0,
                  chapters: (progress.chapters || []).map(ch => ({
                    id: ch.chapterId,
                    title: ch.chapterTitle,
                    wordCount: ch.wordCount,
                  })),
                  coverUrl: undefined,
                  fullText: '',
                  source: 'txt' as const,
                  warnings: undefined,
                };
                return (
                  <BookCard
                    key={progress.bookId}
                    book={isImported ? (book as StoredProcessedBook) : (libraryBookLike as any)}
                    progress={progress}
                    onContinueReading={handleContinueReading}
                    onDelete={async (bookId) => {
                      try {
                        await deleteBook(bookId);
                        await refreshProgress();
                      } catch (err) {
                        console.error('Failed to delete book:', err);
                      }
                    }}
                  />
                );
              })
            )}
          </div>
        )}

      </section>
    </div>
    </div>
  );
};

export default LibraryPage;
